/**
 * DAC profiles -- group 00 (reference exemplars).
 *
 * Every entry below states the facts that actually distinguish the device
 * (converter core, output topology, published dynamic range / THD+N, output
 * voltage, filter set) and lets the kit derive the multidimensional DSP
 * fingerprint from them. Where a figure comes from a datasheet it is tagged
 * MANUFACTURER_SPEC via `confidence`; the derived DSP behaviour is DERIVED or
 * MODELLED. Nothing here is claimed as a measurement.
 */

import type { DacProfile } from '../../types/hardware';
import {
  AKM_FILTERS,
  ESS_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  TUBE_LINE,
  makeDac,
  usd,
} from '../kit';

export const DACS_00: DacProfile[] = [
  makeDac({
    mfr: 'Topping',
    model: 'D90 AK4499',
    price: usd(899, 'ORIGINAL_RETAIL', 2020, 'Launched below the $1,000 line; listed here because the spec names it as the default DAC.'),
    year: 2020,
    country: 'China',
    architecture: 'delta-sigma',
    core: 'AKM AK4499EQ current-output quad-DAC',
    outputStage: 'op-amp',
    dynamicRangeDb: 130,
    thdPlusNDb: -121,
    crosstalkDb: -145,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.2,
    outputImpedanceOhms: 20,
    balanced: true,
    dsd: 'dsd512',
    filters: AKM_FILTERS(),
    jitterPsRms: 20,
    noiseShaperOrder: 6,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fb8ff',
    tags: ['reference', 'default-system', 'akm'],
    notes:
      'AK4499EQ in current-output mode with a discrete I/V front end. Measured performance is at the state of the art for THD+N and channel separation, so the audible fingerprint of this profile is deliberately close to a straight wire; it is the natural control condition for A/B work.',
    provenanceNote:
      'Dynamic range, THD+N and channel separation from Topping published specifications for the AK4499 revision. Filter set follows the AK4499EQ datasheet roll-off options.',
  }),

  makeDac({
    mfr: 'Chord Electronics',
    model: 'DAVE',
    price: usd(13500, 'MSRP', 2024),
    year: 2016,
    country: 'United Kingdom',
    architecture: 'fpga-pulse-array',
    core: 'Xilinx XC7A200T FPGA, 164,000-tap WTA filter, 20-element Pulse Array',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 127,
    thdPlusNDb: -120,
    crosstalkDb: -138,
    outputVrmsSE: 3.0,
    outputVrmsBal: 6.0,
    outputImpedanceOhms: 0.055,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000, 705600, 768000],
    filters: [
      { ...FILTERS.brickwall(2048), id: 'wta-164k', label: 'WTA 164,000-tap (fixed)', stopbandDb: -160, cutoffRatio: 0.955, transitionRatio: 0.035 },
    ],
    jitterPsRms: 8,
    internalOversampling: 256,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#ffd166',
    tags: ['fpga', 'flagship', 'rob-watts'],
    notes:
      'Chord does not use an off-the-shelf converter chip: a very long windowed-sinc (WTA) interpolation filter feeds a 20-element pulse-array modulator. The practical consequence modelled here is an unusually long, very low-ripple reconstruction filter with a near-brick-wall transition and correspondingly extended pre/post ringing on band-edge transients.',
    provenanceNote:
      'Tap count, architecture and output impedance from Chord published material. THD+N and dynamic range from manufacturer specification.',
  }),

  makeDac({
    mfr: 'Denafrips',
    model: 'Terminator II',
    price: usd(4498, 'CURRENT_RETAIL', 2023),
    year: 2020,
    country: 'China',
    architecture: 'discrete-r2r',
    core: 'Discrete 26-bit R-2R ladder, 4 x resistor networks, FPGA control',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 122,
    thdPlusNDb: -106,
    crosstalkDb: -110,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 625,
    balanced: true,
    dsd: 'dsd1024',
    filters: [FILTERS.nosCompensated(), FILTERS.sharpLinear(128), FILTERS.slowLinear(48)],
    defaultFilterId: 'nos-comp',
    ladderMismatchLsb: 0.55,
    effectiveBits: 21,
    jitterPsRms: 45,
    harmonicShape: HARMONIC_SHAPES.r2r,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0a17a',
    tags: ['r2r', 'nos', 'discrete'],
    notes:
      'Discrete ladder conversion with selectable oversampling. In NOS mode the zero-order hold imposes a sin(x)/x droop (-3.9 dB at Nyquist at 44.1 kHz) which this profile applies as a rate-dependent response; ladder element mismatch is modelled as level-dependent INL, which is why its distortion signature does not fall away at low level the way a delta-sigma design does.',
    provenanceNote:
      'Architecture, output impedance and rate support from Denafrips published specifications. INL magnitude is an estimate typical of discrete ladder implementations at this price point.',
  }),

  makeDac({
    mfr: 'Lampizator',
    model: 'Baltic 4',
    price: usd(9800, 'MSRP', 2023),
    year: 2022,
    country: 'Poland',
    architecture: 'discrete-r2r',
    core: 'Selectable R-2R / DSD direct conversion, tube output',
    outputStage: 'tube',
    dynamicRangeDb: 112,
    thdPlusNDb: -74,
    crosstalkDb: -82,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 1200,
    balanced: true,
    dsd: 'dsd512',
    filters: [FILTERS.nos(), FILTERS.slowMinimum(32)],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.8,
    effectiveBits: 19,
    jitterPsRms: 90,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(9, -1.1),
    slewRateVus: 12,
    confidence: 'ESTIMATED',
    accent: '#ff9f6e',
    tags: ['tube', 'r2r', 'nos', 'directly-heated'],
    notes:
      'Directly-heated triode output stage with no global feedback. The dominant audible signature modelled here is a strongly second-harmonic-biased transfer curve whose distortion rises with level, a relatively high output impedance that interacts with the following input stage, and a gentle HF softening from the output coupling.',
    provenanceNote:
      'Topology from manufacturer description. Distortion and noise figures are estimates typical of no-feedback directly-heated triode output stages; Lampizator does not publish THD.',
  }),
];
